import { useState, useEffect } from 'react';
import AnimatedPage from '@/components/AnimatedPage';
import { GlassCard, GlassCardContent, GlassCardDescription, GlassCardHeader, GlassCardTitle } from '@easyflow/ui-master/components/ui/glass-card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Input } from '@easyflow/ui-master/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { SkeletonMetricGrid, SkeletonTable } from '@easyflow/ui-master/components/ui/skeleton-loader';
import { DollarSign, Phone, Clock, Building2, RefreshCw, Search, TrendingUp, PhoneIncoming, PhoneOutgoing, AlertTriangle, ShieldCheck } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import type {
  MasterBillingOverviewRow as BillingData,
  MasterBillingStats as BillingStats,
  MasterEmbeddingHealth as EmbeddingHealth,
} from '@easyflow/shared';
import { toErr } from '@easyflow/shared';

export default function BillingMaster() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [billingData, setBillingData] = useState<BillingData[]>([]);
  const [stats, setStats] = useState<BillingStats | null>(null);
  const [embHealth, setEmbHealth] = useState<EmbeddingHealth | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [dateRange, setDateRange] = useState<{ start: string; end: string } | null>(null);

  useEffect(() => {
    void loadData();
  }, [dateRange]);

  const loadData = async () => {
    try {
      setIsLoading(true);

      const queryString = dateRange
        ? `?start_date=${dateRange.start}&end_date=${dateRange.end}`
        : '';

      const [overviewRes, statsRes, embRes] = await Promise.all([
        apiClient.get<BillingData[]>(`/master/billing/overview${queryString}`),
        apiClient.get<BillingStats>(`/master/billing/stats${queryString}`),
        apiClient.get<EmbeddingHealth>(`/master/embeddings/health`).catch(() => null),
      ]);

      // Lettura single-level: il backend risponde `{ success, data }`.
      setBillingData(Array.isArray(overviewRes.data) ? overviewRes.data : []);
      setStats(statsRes.data ?? null);
      setEmbHealth(embRes?.data ?? null);
    } catch (error: unknown) {
      const __error = toErr(error);
      toast({
        title: 'Error',
        description: __error.message || 'Failed to load billing data',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const filteredData = billingData.filter(
    (item) =>
      item.tenant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.tenant_slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDuration = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  };

  const formatMinutes = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  if (isLoading) {
    return (
      <div className="space-y-6 p-6">
        <div className="mb-6">
          <div className="h-10 w-96 bg-white/20 rounded animate-pulse mb-2" />
          <div className="h-4 w-128 bg-white/10 rounded animate-pulse" />
        </div>
        <SkeletonMetricGrid cols={4} />
        <SkeletonTable rows={10} />
      </div>
    );
  }

  return (
    <AnimatedPage>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Billing & Usage
            </h1>
            <p className="text-muted-foreground dark:text-muted-foreground/70 mt-1">Cross-tenant usage analytics and billing overview</p>
          </div>
          <Button onClick={loadData} variant="outline">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <GlassCard className="border-l-4 border-l-blue-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Total Calls
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl">{(stats.total_calls || 0).toLocaleString()}</GlassCardTitle>
                <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">
                  {stats.answer_rate || 0}% answer rate
                </div>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-green-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <TrendingUp className="h-4 w-4" />
                  Answered Calls
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-green-600">
                  {(stats.answered_calls || 0).toLocaleString()}
                </GlassCardTitle>
                <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">
                  {(stats.missed_calls || 0).toLocaleString()} missed
                </div>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-purple-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Total Duration
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-purple-600">
                  {formatMinutes(stats.total_duration_minutes)}
                </GlassCardTitle>
                <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">
                  Avg: {Math.round(stats.avg_duration_seconds / 60)}m/call
                </div>
              </GlassCardHeader>
            </GlassCard>

            <GlassCard className="border-l-4 border-l-orange-500">
              <GlassCardHeader className="pb-3">
                <GlassCardDescription className="flex items-center gap-2">
                  <Building2 className="h-4 w-4" />
                  Active Tenants
                </GlassCardDescription>
                <GlassCardTitle className="text-3xl text-orange-600">
                  {stats.active_tenants}
                </GlassCardTitle>
                <div className="text-xs text-muted-foreground dark:text-muted-foreground/70">
                  with call activity
                </div>
              </GlassCardHeader>
            </GlassCard>
          </div>
        )}

        {/* Embeddings Dimension Health */}
        {embHealth && (
          <GlassCard className={`border-l-4 ${embHealth.last_24h.total > 0 ? 'border-l-amber-500' : 'border-l-emerald-500'}`}>
            <GlassCardHeader>
              <GlassCardTitle className="flex items-center gap-2 text-base">
                {embHealth.last_24h.total > 0 ? (
                  <AlertTriangle className="h-4 w-4 text-amber-500" />
                ) : (
                  <ShieldCheck className="h-4 w-4 text-emerald-500" />
                )}
                Embeddings — Dimension Health
              </GlassCardTitle>
              <GlassCardDescription>
                Expected <span className="font-mono">{embHealth.expected_dim}d</span> · model{' '}
                <span className="font-mono">{embHealth.current_model || '—'}</span>{' '}
                <Badge variant="secondary">{embHealth.current_provider || '—'}</Badge> · policy{' '}
                <Badge variant={embHealth.policy === 'strict' ? 'default' : 'destructive'}>
                  {embHealth.policy}
                </Badge>
              </GlassCardDescription>
            </GlassCardHeader>
            <GlassCardContent>
              {embHealth.last_24h.total === 0 ? (
                <div className="text-sm text-emerald-600 dark:text-emerald-400">
                  Nessun mismatch nelle ultime 24h. Tutti gli embedding rispettano la dimensione attesa.
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="text-sm">
                    <span className="font-semibold text-amber-600">
                      {embHealth.last_24h.total} mismatch
                    </span>{' '}
                    nelle ultime 24h
                    {Object.keys(embHealth.last_24h.by_action).length > 0 && (
                      <>
                        {' '}(
                        {Object.entries(embHealth.last_24h.by_action)
                          .map(([action, n]) => `${action}: ${n}`)
                          .join(', ')}
                        )
                      </>
                    )}
                  </div>
                  {embHealth.last_24h.by_model.length > 0 && (
                    <div className="text-xs text-muted-foreground dark:text-muted-foreground/70 space-y-1">
                      {embHealth.last_24h.by_model.slice(0, 5).map(m => (
                        <div key={`${m.provider}/${m.model}`}>
                          <span className="font-mono">{m.provider}/{m.model}</span>{' '}
                          — {m.count} eventi (ultimo: {new Date(m.last_seen).toLocaleString()})
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </GlassCardContent>
          </GlassCard>
        )}

        {/* Billing Table */}
        <GlassCard>
          <GlassCardHeader>
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground/70 dark:text-muted-foreground h-4 w-4" />
                <Input
                  placeholder="Search by tenant name or slug..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </GlassCardHeader>
          <GlassCardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Tenant</TableHead>
                    <TableHead className="text-center">Total Calls</TableHead>
                    <TableHead className="text-center">
                      <PhoneIncoming className="h-4 w-4 inline mr-1" />
                      Inbound
                    </TableHead>
                    <TableHead className="text-center">
                      <PhoneOutgoing className="h-4 w-4 inline mr-1" />
                      Outbound
                    </TableHead>
                    <TableHead>Total Duration</TableHead>
                    <TableHead>Outbound Duration</TableHead>
                    <TableHead className="text-center">Extensions</TableHead>
                    <TableHead className="text-center">Trunks</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center py-8 text-muted-foreground dark:text-muted-foreground/70">
                        No billing data found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredData.map((item) => (
                      <TableRow key={item.tenant_id} className="hover:bg-muted/50 dark:hover:bg-white/5">
                        <TableCell>
                          <div>
                            <div className="font-medium">{item.tenant_name}</div>
                            <div className="text-sm text-muted-foreground dark:text-muted-foreground/70">{item.tenant_slug}</div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center font-semibold">
                          {(item.total_calls || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-green-600 font-medium">
                            {(item.inbound_calls || 0).toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-blue-600 font-medium">
                            {(item.outbound_calls || 0).toLocaleString()}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">
                              {formatMinutes(item.total_duration_minutes)}
                            </span>
                            <span className="text-xs text-muted-foreground dark:text-muted-foreground/70">
                              ({(item.answered_calls || 0).toLocaleString()} answered)
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-muted-foreground dark:text-muted-foreground/60">
                            {formatMinutes(item.outbound_duration_minutes)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{item.active_extensions}</Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge variant="secondary">{item.active_trunks}</Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>
    </AnimatedPage>
  );
}
